import { COUNTRY_CODES, SUPPORT_ACTIVITIES, TABLES_KEYS } from 'features/command-center/constants';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { hasFeatureFlag } from 'UI/utils';
import { OptionRenderers } from 'UI/utils/renderers';

export const PAGE_KEY = TABLES_KEYS.overallPerformanceReport;

const COPIES = strings.commandCenter.dailyPerformance.overallPerformanceReport;

export const FiltersGroups = [
  {
    id: 'recruiters',
    name: 'Recruiters',
    filters: [
      'employeeNumbers',
      'teams',
      'healthStatuses',
      'channelPartnersEmpNumbers',
      'startDate',
      'directors',
      'recruitedBy',
      'lastFeedbackActivityTypes',
      'lastFeedbackActivityDate',
      'countryCode'
    ]
  },
  {
    id: 'sendouts',
    name: 'Sendouts',
    filters: [
      'rangeByCurrentWeekSendouts',
      'rangeByLastWeekSendouts',
      'rangeByThirdWeekSendouts',
      'rangeByFourthWeekSendouts',
      'rangeByFifthWeekSendouts',
      'rangeBySendoutPlacementRatio',
      'rangeByActiveSendouts',
      'rangeByLastFiveWeeksSendouts',
      'rangeByCurrentWeekSendovers',
      'rangeByActiveSendovers'
    ]
  },
  {
    id: 'financial',
    name: 'Financial',
    filters: [
      'rangeByOutstandingBillings',
      'rangeByDrawBalance',
      'rangeByAvgFeePercentage',
      'rangeByAvgFee',
      'rangeByCashin',
      'rangeByCareerCashin',
      'rangeByYearlyProduction',
      'rangeByTrackingProduction'
    ],
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    id: 'metrics',
    name: 'Metrics',
    filters: [
      'rangeByInMails',
      'rangeByInMailsByBillingPeriod',
      'rangeByBulks',
      'rangeByPeopleReached',
      'rangeByPosts',
      'rangeByNewLivePosts',
      'rangeByPhoneTime',
      'rangeByConnectedCalls',
      'rangeByNotConnectedCalls',
      'rangeByTotalCalls'
    ],
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    id: 'inventory',
    name: 'Inventory',
    filters: [
      'rangeByNotSignedCompaniesJobOrders',
      'rangeBySignedCompaniesJobOrders',
      'rangeByCandidates',
      'rangeByContacts',
      'rangeByHiringAuthorities',
      'rangeByCompanies'
    ]
  }
];

const SUPPORT_ACTIVITY_COLUMNS = hasFeatureFlag(
  FeatureFlags.OverallPerformanceFeedbackActivityColumns
)
  ? [
      {
        name: 'lastFeedbackActivityType',
        label: 'Support Activity Type',
        options: {
          sort: true,
          display: true
        }
      },
      {
        name: 'lastFeedbackActivityDate',
        label: 'Support Activity Date',
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackActivityGiverName',
        label: 'Support Activity By',
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

export const COLUMNS = [
  {
    name: 'idFortpac',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'empNo',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'recruiterName',
    label: 'Name',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewRecruiter,
          idKey: 'empNo',
          textKey: 'name',
          enableInTabRedirect: true
        }
      },
      setCellHeaderProps: () => ({ className: 'dataTableStickyLeftColumnHeader' }),
      setCellProps: () => ({
        className: 'dataTableStickyLeftColumn'
      })
    }
  },
  {
    name: 'managerName',
    label: 'Team',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewTeam,
          idKey: 'managerEmpNo',
          textKey: 'team',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'healthStatus',
    label: 'Current Health Status',
    options: {
      sort: true,
      display: true,
      renderer: 'healthStatusChip'
    }
  },
  {
    name: 'hireDate',
    label: 'Start Date',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'directorName',
    label: 'Director',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'teamSupervisorsNames',
    label: 'Support Team',
    options: {
      sort: false,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 200 }
      })
    }
  },
  {
    name: 'lastPlacementDate',
    label: 'Last Placement',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'thisWeekSendouts',
    label: 'Current Week Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastWeekSendouts',
    label: 'Last Week Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'thirdWeekSendouts',
    label: 'Prev. Week 2 Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'fourthWeekSendouts',
    label: 'Prev. Week 3 Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'fifthWeekSendouts',
    label: 'Prev. Week 4 Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'activeSendouts',
    label: 'Active Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastFiveWeeksSendouts',
    label: 'Last Five Weeks Sendouts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'soPlRatio',
    label: 'SO to PL',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'currentWeekActiveSendovers',
    label: 'Current Week Sendovers',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'activeSendovers',
    label: 'Active Sendovers',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'avgFeePercentage',
    label: 'Avg Fee % YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'percentage'
    }
  },
  {
    name: 'avgFeeMoney',
    label: 'Avg Fee YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'outstandingBillings',
    label: 'Current O. Billings',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'drawBalance',
    label: 'Current Draw Balance',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashin',
    label: 'Cash In YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'production',
    label: 'Production YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'trackingProduction',
    label: 'Tracking Production',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'avgInMailsByBillingPeriod',
    label: 'InMails',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'inMailsByBillingPeriod',
    label: 'Billing P. InMails',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'bulks',
    label: 'Bulks',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'peopleReached',
    label: 'People Reached',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'todayLivePosts',
    label: 'Prev. Day Active Posts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'newLivePosts',
    label: 'New Posts per Day',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'callsTime',
    label: 'Phone Time',
    options: {
      sort: true,
      display: true,
      renderer: 'phoneTime'
    }
  },
  {
    name: 'calls',
    label: 'Calls',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notConnectedCalls',
    label: 'Not Conn. Calls',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'totalCalls',
    label: 'Total Calls',
    options: {
      sort: false,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notSignedCompaniesJo',
    label: 'Not Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'signedCompaniesJo',
    label: 'Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'candidatesAdded',
    label: 'Candidates',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'contactsAdded',
    label: 'Contacts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'haAdded',
    label: 'Hiring Authorities',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'companiesAdded',
    label: 'Companies',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'careerCashin',
    label: 'Career Cash in',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...SUPPORT_ACTIVITY_COLUMNS,
  {
    name: 'recruitedBy',
    label: 'Recruited By',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner1Name',
    label: 'Channel P. 1',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner2Name',
    label: 'Channel P. 2',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'location',
    label: 'Location',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'countryCode',
    label: 'Country Code',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastFeedbackNoteDate',
    label: 'Last Doc Date',
    options: {
      sort: true,
      display: true,
      renderer: 'date'
    }
  },
  {
    name: 'lastFeedbackNoteGiverName',
    label: 'Last Doc By',
    options: {
      sort: true,
      display: true
    }
  }
];

export const ORDER_BY_OPTIONS = {
  column: 'recruiterName',
  direction: 'asc'
};

export const INLINE_FILTERS = ['team', 'healthStatus'];

export const METRICS_DESCRIPTION = [
  COPIES.footer.metrics.inMails,
  COPIES.footer.metrics.SOtoPL,
  COPIES.footer.metrics.calls,
  COPIES.footer.metrics.posts
];

export const extraFilters = [
  {
    name: 'rangeByCurrentWeekSendouts',
    paramName: 'currentWeekSendouts',
    title: COPIES.filters.sendouts.currentWeek,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.currentWeek
  },
  {
    name: 'rangeByLastWeekSendouts',
    paramName: 'lastWeekSendouts',
    title: COPIES.filters.sendouts.lastWeek,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.lastWeek
  },
  {
    name: 'rangeByThirdWeekSendouts',
    paramName: 'thirdWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekTwo,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekTwo
  },
  {
    name: 'rangeByFourthWeekSendouts',
    paramName: 'fourthWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekThree,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekThree
  },
  {
    name: 'rangeByFifthWeekSendouts',
    paramName: 'fifthWeekSendouts',
    title: COPIES.filters.sendouts.previousWeekFour,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.previousWeekFour
  },
  {
    name: 'rangeBySendoutPlacementRatio',
    paramName: 'sendoutToPlacementRatio',
    title: COPIES.filters.sendouts.placementRatio,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.placementRatio
  },
  {
    name: 'rangeByActiveSendouts',
    paramName: 'activeSendouts',
    title: COPIES.filters.sendouts.active,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.active
  },
  {
    name: 'rangeByLastFiveWeeksSendouts',
    paramName: 'lastFiveWeeksSendouts',
    title: COPIES.filters.sendouts.lastFiveWeeks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.lastFiveWeeks
  },
  {
    name: 'rangeByCurrentWeekSendovers',
    paramName: 'currentWeekActiveSendovers',
    title: COPIES.filters.sendouts.currentWeekSendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.currentWeekSendovers
  },
  {
    name: 'rangeByActiveSendovers',
    paramName: 'activeSendovers',
    title: COPIES.filters.sendouts.activeSendovers,
    type: FilterType.Range,
    displayLabel: COPIES.filters.sendouts.activeSendovers
  },
  {
    name: 'rangeByOutstandingBillings',
    paramName: 'outstandingBillings',
    title: COPIES.filters.financial.outstandingBillings,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.outstandingBillings,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    name: 'rangeByDrawBalance',
    paramName: 'drawBalance',
    title: COPIES.filters.financial.drawBalance,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.drawBalance,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    name: 'rangeByAvgFeePercentage',
    paramName: 'avgFeePercentage',
    title: COPIES.filters.financial.avgFeePercentage,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFeePercentage,
    inputType: 'percentage'
  },
  {
    name: 'rangeByAvgFee',
    paramName: 'avgFeeMoney',
    title: COPIES.filters.financial.avgFee,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.avgFee,
    inputType: 'currency'
  },
  {
    name: 'rangeByCashin',
    paramName: 'cashin',
    title: COPIES.filters.financial.cashin,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.cashin,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    name: 'rangeByCareerCashin',
    paramName: 'careerCashin',
    title: COPIES.filters.financial.careerCashIn,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.careerCashIn,
    inputType: 'currency'
  },
  {
    name: 'rangeByYearlyProduction',
    paramName: 'production',
    title: COPIES.filters.financial.yearlyProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.yearlyProduction,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    name: 'rangeByTrackingProduction',
    paramName: 'trackingProduction',
    title: COPIES.filters.financial.trackingProduction,
    type: FilterType.Range,
    displayLabel: COPIES.filters.financial.trackingProduction,
    inputType: 'currency',
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceFinancialFilters)
  },
  {
    name: 'rangeByInMailsByBillingPeriod',
    paramName: 'inMailsByBillingPeriod',
    title: COPIES.filters.metrics.billingPeriodInMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.billingPeriodInMails,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByInMails',
    paramName: 'inMails',
    title: COPIES.filters.metrics.inMails,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.inMails,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByBulks',
    paramName: 'bulks',
    title: COPIES.filters.metrics.bulks,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.bulks,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByPeopleReached',
    paramName: 'peopleReached',
    title: COPIES.filters.metrics.peopleReached,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.peopleReached,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByPosts',
    paramName: 'posts',
    title: COPIES.filters.metrics.posts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.posts,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByNewLivePosts',
    paramName: 'newLivePosts',
    title: COPIES.filters.metrics.newLivePosts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.newLivePosts
  },
  {
    name: 'rangeByPhoneTime',
    paramName: 'phoneTime',
    title: COPIES.filters.metrics.phoneTime,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.phoneTime,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByConnectedCalls',
    paramName: 'connectedCalls',
    title: COPIES.filters.metrics.connectedCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.connectedCalls,
    showWhen: () => hasFeatureFlag(FeatureFlags.DailyPerformanceMetricsFilters)
  },
  {
    name: 'rangeByNotConnectedCalls',
    paramName: 'notConnectedCalls',
    title: COPIES.filters.metrics.notConnectedCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.notConnectedCalls
  },
  {
    name: 'rangeByTotalCalls',
    paramName: 'totalCalls',
    title: COPIES.filters.metrics.totalCalls,
    type: FilterType.Range,
    displayLabel: COPIES.filters.metrics.totalCalls
  },
  {
    name: 'lastFeedbackActivityTypes',
    paramName: 'lastFeedbackActivityTypes',
    title: COPIES.filters.recruiters.lastSupportActivity,
    type: FilterType.Select,
    options: SUPPORT_ACTIVITIES,
    renderOption: OptionRenderers.title,
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFeedbackActivityColumns)
  },
  {
    name: 'lastFeedbackActivityDate',
    paramName: 'lastFeedbackActivityDate',
    title: COPIES.filters.recruiters.lastSupportActivityDate,
    type: 'date',
    disableTimeRange: true,
    showWhen: () => hasFeatureFlag(FeatureFlags.OverallPerformanceFeedbackActivityColumns)
  },
  {
    name: 'recruitedBy',
    paramName: 'recruitedBy',
    title: 'Recruited By',
    type: FilterType.Input
  },
  {
    name: 'channelPartnersEmpNumbers',
    paramName: 'channelPartners',
    title: COPIES.filters.recruiters.channelPartner,
    type: FilterType.Autocomplete,
    displayKey: 'full_name',
    url: `${Endpoints.Users}?extraColumns=external_employee_id`,
    multiple: true,
    idKey: 'external_employee_id'
  },
  {
    name: 'rangeByNotSignedCompaniesJobOrders',
    paramName: 'notSignedCompaniesJo',
    title: COPIES.filters.inventory.notSignedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.notSignedCompaniesJobOrders
  },
  {
    name: 'rangeBySignedCompaniesJobOrders',
    paramName: 'signedCompaniesJo',
    title: COPIES.filters.inventory.signedCompaniesJobOrders,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.signedCompaniesJobOrders
  },
  {
    name: 'rangeByCandidates',
    paramName: 'candidatesAdded',
    title: COPIES.filters.inventory.candidates,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.candidates
  },
  {
    name: 'rangeByContacts',
    paramName: 'contactsAdded',
    title: COPIES.filters.inventory.contacts,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.contacts
  },
  {
    name: 'rangeByHiringAuthorities',
    paramName: 'haAdded',
    title: COPIES.filters.inventory.hiringAuthorities,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.hiringAuthorities
  },
  {
    name: 'rangeByCompanies',
    paramName: 'companiesAdded',
    title: COPIES.filters.inventory.companies,
    type: FilterType.Range,
    displayLabel: COPIES.filters.inventory.companies
  },
  {
    name: 'countryCode',
    paramName: 'countryCode',
    title: COPIES.filters.recruiters.country,
    type: FilterType.Select,
    options: COUNTRY_CODES,
    renderOption: OptionRenderers.title
  }
];
